import { computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';

export interface SubmitPublicReviewPayload {
  token: string;
  quality: number; // 1-5
  communication: number; // 1-5
  timelines: number; // 1-5
  value_for_money: number; // 1-5
  review_text: string; // min 10 characters
  is_anonymous?: boolean;
  project_value_range_id?: number;
}

/**
 * Composable for submitting public reviews
 * Handles review submission with error and success notifications
 */
export function useSubmitPublicReview() {
  const api = useApi();
  const toaster = useToaster();

  const submitMutation = useMutation({
    mutationKey: ['public', 'submitReview'],
    mutationFn: async (payload: SubmitPublicReviewPayload) => {
      return await api.public.reviewsV2.submitReview({
        body: payload
      });
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review submitted successfully!',
        description: 'Thank you for taking the time to share your feedback. Your review helps other clients make informed decisions.',
        type: 'success',
      });
    },
    onError: (error: any) => {
      console.error('[useSubmitPublicReview] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to submit review. Please try again.';
      
      toaster.create({
        title: 'Error submitting review',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isSubmitting = computed(() => submitMutation.isPending.value);
  const isSuccess = computed(() => submitMutation.isSuccess.value);
  const isError = computed(() => submitMutation.isError.value);

  const submitReview = (payload: SubmitPublicReviewPayload) => {
    return submitMutation.mutateAsync(payload);
  };

  return {
    submitMutation,
    isSubmitting,
    isSuccess,
    isError,
    submitReview,
  };
}

